import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, CalendarPlus } from 'lucide-react'
import { apiJson } from '../api'

interface LeaveBalance {
  leaveType: string
  entitled: number | null
  used: number
  remaining: number | null
}

const LEAVE_TYPES = ['annual', 'casual', 'sick', 'unpaid']

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

const emptyForm = { leaveType: 'annual', startDate: '', endDate: '', reason: '' }

export default function LeaveRequest() {
  const navigate = useNavigate()
  const [form, setForm] = useState(emptyForm)
  const [balances, setBalances] = useState<LeaveBalance[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    apiJson<LeaveBalance[]>('/leave-balances/me')
      .then(setBalances)
      .catch(() => {})
  }, [])

  const daysPreview = useMemo(() => {
    if (!form.startDate || !form.endDate) return null
    const start = new Date(form.startDate)
    const end = new Date(form.endDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null
    return Math.round((end.getTime() - start.getTime()) / 86400000) + 1
  }, [form.startDate, form.endDate])

  const submit = async () => {
    setSaving(true)
    setError('')
    try {
      await apiJson('/leave-requests', {
        method: 'POST',
        body: JSON.stringify({
          leaveType: form.leaveType,
          startDate: form.startDate,
          endDate: form.endDate,
          ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
        }),
        headers: { 'Content-Type': 'application/json' },
      })
      navigate('/leave')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed')
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <Link to="/leave" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Leave
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <CalendarPlus size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">Request Leave</h1>
            <p className="text-xs text-slate-400">Submits for approval — a Camunda workflow tracks the review</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Leave type</label>
            <select value={form.leaveType} onChange={(e) => setForm({ ...form, leaveType: e.target.value })} className={inputCls}>
              {LEAVE_TYPES.map((t) => (
                <option key={t} value={t} className="capitalize">{t}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start date *</label>
              <input required type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End date *</label>
              <input required type="date" min={form.startDate || undefined} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className={inputCls} />
            </div>
          </div>
          {daysPreview !== null && (() => {
            const bal = balances.find((b) => b.leaveType === form.leaveType)
            const over = bal?.remaining !== null && bal?.remaining !== undefined && daysPreview > bal.remaining
            return (
              <p className={`rounded-xl px-4 py-2.5 text-xs font-semibold ${over ? 'bg-red-50 text-red-600' : 'bg-brand-50 text-brand-700'}`}>
                {daysPreview} day{daysPreview === 1 ? '' : 's'} of leave
                {bal && bal.remaining !== null && ` · ${bal.remaining} day(s) remaining this year`}
                {over && ' — exceeds your balance; approvers will be blocked'}
              </p>
            )
          })()}
          <div>
            <label className={labelCls}>Reason</label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              maxLength={500}
              placeholder="Optional context for the approver…"
              className={`${inputCls} resize-none`}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/leave"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || daysPreview === null}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
